import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { ApiError, apiFetch } from "@/lib/api";
import type { DocumentResource, Page, ReferenceListItemResource } from "@/lib/api-types";
import { labelFor } from "@/lib/field-labels";

/**
 * `PropertyRecord.tsx`'s own "Add document" action (Story 7.2, judgement
 * call 2) — a single-step dialog mirroring `AddTenantDialog.tsx`'s own
 * "plain one-step form" shape, not `AddPropertyWizard.tsx`'s multi-step one
 * (this AC names no multi-step flow).
 *
 * Uploads AND attaches in one real, atomic call: `POST /documents` (Story
 * 7.1's own raw-bytes-as-body shape, never `multipart/form-data` —
 * `apiFetch`'s own body accepts a `File` directly, since `File` IS a
 * `Blob`) with `X-PropDesk-Entity-Type`/`X-PropDesk-Entity-Id` set to this
 * property, plus the optional category the Owner picked. There is no
 * separate "attach" step this dialog could leave half-done — the same
 * "upload and attach together, never a two-step upload-then-PATCH" shape
 * `app/api/documents.py`'s own module docstring commits to.
 */
export function AddDocumentDialog({
  open,
  onOpenChange,
  propertyId,
  onUploaded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  onUploaded: (document: DocumentResource) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState("");
  // Story 7.3: an optional expiration date, settable right here at upload
  // time (the AC's own "uploaded or edited" wording) — mirrors `category`'s
  // identical "optional, blank means not set" shape.
  const [expirationDate, setExpirationDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const categoriesQuery = useQuery({
    queryKey: ["reference-list-items", "document_category"],
    queryFn: () => apiFetch<Page<ReferenceListItemResource>>("/reference-list-items?list_key=document_category"),
  });
  const categoryOptions = categoriesQuery.data?.data ?? [];

  useEffect(() => {
    if (!open) {
      setFile(null);
      setCategory("");
      setExpirationDate("");
      setError(undefined);
    }
  }, [open]);

  const canSubmit = file !== null;

  async function handleSubmit() {
    if (file === null) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    try {
      const headers: Record<string, string> = {
        // Tier B fix round, 2026-08-20, Fix 2: `file.name` set verbatim as a
        // raw header value throws a synchronous `TypeError` inside the
        // browser's own `Headers` constructor for any filename containing a
        // code point above 255 (WHATWG Fetch/WebIDL) — before this request
        // is even sent. A completely ordinary filename (CJK, Arabic,
        // Hebrew, Cyrillic, Greek, most emoji — the kind a phone's camera
        // app appends on its own) could never be uploaded. Percent-encoded
        // here, decoded server-side (`app/api/documents.py::
        // _decode_filename_header`) before `_clean_filename` runs — mirrors
        // how `Content-Disposition` response headers already solve the
        // mirror-image problem for non-ASCII text in an HTTP header value.
        "X-PropDesk-Filename": encodeURIComponent(file.name),
        "X-PropDesk-Entity-Type": "property",
        "X-PropDesk-Entity-Id": propertyId,
      };
      if (category !== "") {
        headers["X-PropDesk-Category"] = category;
      }
      if (expirationDate !== "") {
        headers["X-PropDesk-Expiration-Date"] = expirationDate;
      }
      const uploaded = await apiFetch<DocumentResource>("/documents", {
        method: "POST",
        headers,
        body: file,
      });
      onOpenChange(false);
      onUploaded(uploaded);
    } catch (submitError) {
      setError(
        submitError instanceof ApiError ? submitError.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Add a document">
      <form
        className="mt-4 flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">File</span>
          <Input
            type="file"
            autoFocus
            accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("category")}</span>
          <Select value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="">Uncategorised</option>
            {categoryOptions.map((option) => (
              <option key={option.id} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">
            {labelFor("expiration_date")} (optional)
          </span>
          <Input
            type="date"
            value={expirationDate}
            onChange={(event) => setExpirationDate(event.target.value)}
          />
        </label>

        {error ? (
          <p role="alert" className="text-meta text-danger">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? "Uploading…" : "Add document"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
